// REL-02: shared, testable runtime-config validation used by BOTH the Vite
// build plugin (build-time, to refuse shipping a misconfigured config.json) and
// the SPA at load time (AuthGate, to fail closed on an unconfigured deploy).
//
// SPA config carries no secrets, but a config pointing at the wrong environment
// is a cross-environment hazard, so validation is explicit and fail-closed.

export type AuthMode = "oidc" | "cognito-password" | "static-token" | "unconfigured";

export interface RuntimeConfigShape {
  apiBaseUrl?: unknown;
  auth?: { mode?: unknown; clientId?: unknown } | undefined;
  adminApiBaseUrl?: unknown;
  environment?: unknown;
  /** B10: owner-supplied legal and support links (https URLs / an email address). */
  legal?: { privacyUrl?: unknown; termsUrl?: unknown; eulaUrl?: unknown } | undefined;
  support?: { email?: unknown; url?: unknown } | undefined;
}

/** The explicit "not configured" sentinel the build emits when RUNTIME_CONFIG is absent. */
export const UNCONFIGURED_RUNTIME_CONFIG = {
  apiBaseUrl: "",
  auth: { mode: "unconfigured" as const },
  adminApiBaseUrl: "",
  environment: "unconfigured",
};

/** True when a loaded config is the unconfigured sentinel (or otherwise unusable). */
export function isUnconfigured(cfg: RuntimeConfigShape): boolean {
  return cfg.environment === "unconfigured" || (cfg.auth?.mode as string | undefined) === "unconfigured" || !cfg.apiBaseUrl;
}

/**
 * Validate an operator-supplied RUNTIME_CONFIG. Returns the list of problems
 * (empty = valid). Build-time callers throw on any problem; the SPA uses
 * isUnconfigured() for the softer "not configured yet" case.
 */
export function runtimeConfigProblems(cfg: RuntimeConfigShape): string[] {
  const problems: string[] = [];
  if (typeof cfg.apiBaseUrl !== "string" || !/^https:\/\//.test(cfg.apiBaseUrl)) {
    problems.push("apiBaseUrl must be an https:// URL");
  }
  const mode = cfg.auth?.mode;
  if (mode !== "oidc" && mode !== "cognito-password" && mode !== "static-token") {
    problems.push(`auth.mode must be oidc | cognito-password | static-token (got ${String(mode)})`);
  }
  if ((mode === "oidc" || mode === "cognito-password") && typeof cfg.auth?.clientId !== "string") {
    problems.push("auth.clientId is required for this auth mode");
  }
  if (typeof cfg.environment !== "string" || cfg.environment === "unconfigured") {
    problems.push("environment must be set (e.g. prod, staging, preview) and not 'unconfigured'");
  }
  for (const k of ["privacyUrl", "termsUrl", "eulaUrl"] as const) {
    const v = cfg.legal?.[k];
    if (v !== undefined && !(typeof v === "string" && /^https:\/\/\S+$/.test(v))) problems.push(`legal.${k} must be an https:// URL`);
  }
  const email = cfg.support?.email;
  if (email !== undefined && !(typeof email === "string" && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))) problems.push("support.email must be an email address");
  const sUrl = cfg.support?.url;
  if (sUrl !== undefined && !(typeof sUrl === "string" && /^https:\/\/\S+$/.test(sUrl))) problems.push("support.url must be an https:// URL");
  return problems;
}
